import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Privacy-minimised material view counter.
 *
 * - records only an aggregate page-view event;
 * - uses a one-visit random id held in memory only;
 * - does not write analytics identifiers to cookies/localStorage/sessionStorage;
 * - does not track scroll depth, pointer activity or cross-visit identity.
 *
 * The backend may use the authenticated request only to verify material access;
 * analytics storage itself must not persist the viewer identity.
 */

function newVisitId() {
  return "v-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
}

async function postView(materialId, visitId) {
  try {
    await api.post(`/materials/${materialId}/events`, {
      event_type: "material_view",
      visit_id: visitId,
    });
  } catch { /* analytics must never interrupt reading */ }
}

export default function useMaterialAnalytics(materialId) {
  const state = useRef({ materialId: null, visitId: null, sent: false });

  useEffect(() => {
    if (!materialId) return;

    if (state.current.materialId !== materialId) {
      state.current = { materialId, visitId: newVisitId(), sent: false };
    }
    if (state.current.sent) return;

    state.current.sent = true;
    postView(materialId, state.current.visitId);
  }, [materialId]);
}
